import { createContext, useState, useMemo } from "react";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import MessageDialog from "../dialogs/MessageDialog";

export type AlertMsg = {
  id: number;
  type: "alert";
  content: string | JSX.Element;
  onClose?: () => void;
};

export type ConfirmMsg = {
  id: number;
  type: "confirm";
  content: string | JSX.Element;
  resolve: (value: boolean | void | PromiseLike<boolean | void>) => void;
  options?: { cancelText?: string };
};

export type AlertContextType = {
  dialog: {
    alert: (
      msg: string | JSX.Element,
      options?: { onClose?: () => void }
    ) => { close: () => void };
    confirm: (
      msg: string,
      options?: { cancelText?: string }
    ) => Promise<boolean | void> & { close: () => void };
  };
};

export const AlertContext = createContext({} as AlertContextType);

let messagesUid = 0;

export const AlertProvider = (props) => {
  const { children } = props;
  const [messages, setMessages] = useState<(AlertMsg | ConfirmMsg)[]>([]);

  const dialog = useMemo((): AlertContextType["dialog"] => {
    return {
      alert: (msg, options) => {
        const msgId = ++messagesUid;
        setMessages((messages) => [
          ...messages,
          {
            id: msgId,
            type: "alert",
            content: msg,
            onClose: options?.onClose,
          },
        ]);
        return {
          close() {
            setMessages((messages) => messages.filter((m) => m.id !== msgId));
          },
        };
      },
      confirm: (msg, options = {}) => {
        const msgId = ++messagesUid;
        return Object.assign(
          new Promise<boolean | void>((resolve) => {
            setMessages((messages) => [
              ...messages,
              { id: msgId, type: "confirm", content: msg, resolve, options },
            ]);
          }),
          {
            close() {
              setMessages((messages) => messages.filter((m) => m.id !== msgId));
            },
          }
        );
      },
    };
  }, []);

  return (
    <AlertContext.Provider value={{ dialog }}>
      {children}
      {messages.map((o) => {
        if (o.type === "alert") {
          return (
            <MessageDialog
              key={o.id}
              text={o.content}
              isOpen={true}
              onClose={() => {
                setMessages((messages) => messages.filter((m) => m !== o));
                if (o.onClose) o.onClose();
              }}
            />
          );
        }
        if (o.type === "confirm") {
          return (
            <ConfirmDialog
              key={o.id}
              cancelText={o.options?.cancelText}
              text={o.content}
              isOpen={true}
              onClose={() => {
                o.resolve(undefined);
                setMessages((messages) => messages.filter((m) => m !== o));
              }}
              onConfirm={() => {
                o.resolve(true);
                setMessages((messages) => messages.filter((m) => m !== o));
              }}
              onCancel={() => {
                o.resolve(false);
                setMessages((messages) => messages.filter((m) => m !== o));
              }}
            />
          );
        }
        return null;
      })}
    </AlertContext.Provider>
  );
};

export const AlertConsumer = AlertContext.Consumer;

export default AlertContext;
